import { CaretUpDown, Check, type Icon } from "@phosphor-icons/react";
import {
  Button as AriaButton,
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  ListBox,
  ListBoxItem,
  SelectValue,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import {
  type BaseFieldProps,
  controlHeight,
  FieldDescription,
  FieldErrorText,
  FieldLabel,
  fieldRoot,
  inputBase,
} from "./field";
import { listPad, optionRow } from "./list-styles";
import { Popover } from "./Popover";

export type Option = { id: string; label: string; icon?: Icon; textValue?: string };

export interface SelectProps
  extends Omit<AriaSelectProps<Option>, "children" | "className" | "items" | "selectedKey" | "onSelectionChange">,
    BaseFieldProps {
  items: Option[];
  selectedKey?: string | null;
  onSelectionChange?: (key: string) => void;
  className?: string;
}

export function Select({
  label,
  labelHidden,
  description,
  error,
  items,
  selectedKey,
  onSelectionChange,
  placeholder,
  className,
  ...props
}: SelectProps) {
  return (
    <AriaSelect
      className={cx("group", fieldRoot, className)}
      selectedKey={selectedKey}
      onSelectionChange={(k) => k != null && onSelectionChange?.(String(k))}
      isInvalid={error ? true : props.isInvalid}
      placeholder={placeholder}
      {...props}
    >
      <FieldLabel hidden={labelHidden}>{label}</FieldLabel>
      <AriaButton
        className={cx(
          inputBase,
          controlHeight,
          "flex items-center justify-between gap-2 text-left data-pressed:bg-hover group-data-invalid:border-input-border-invalid [&_svg]:transition-colors",
        )}
      >
        <SelectValue className="flex min-w-0 flex-1 items-center gap-2 data-placeholder:text-fg-subtle" />
        <CaretUpDown size={14} aria-hidden="true" className="shrink-0 text-fg-subtle" />
      </AriaButton>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldErrorText>{error}</FieldErrorText>
      <Popover className="min-w-(--trigger-width) max-h-72">
        <ListBox items={items} className={listPad}>
          {(o) => <OptionItem option={o} />}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

export function OptionItem({ option }: { option: Option }) {
  const I = option.icon;
  return (
    <ListBoxItem id={option.id} textValue={option.textValue ?? option.label} className={optionRow} data-ring="inset">
      {({ isSelected }) => (
        <>
          {I ? <I size={16} aria-hidden="true" className="shrink-0 text-fg-muted" /> : null}
          <span className="min-w-0 flex-1 truncate-title" title={option.label}>
            {option.label}
          </span>
          {isSelected ? <Check size={14} aria-hidden="true" className="shrink-0 text-accent-text" /> : null}
        </>
      )}
    </ListBoxItem>
  );
}
